import { TitleCasePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { Gtag } from 'angular-gtag';
import { MenuItem } from 'primeng/api';
import { combineLatest, Observable, Subject, takeUntil } from 'rxjs';
import { Patient } from 'src/app/core/models';
import { EnvService, ResponsiveUtilsService, SessionExpirationService } from 'src/app/core/services';
import { UpdatePatient } from 'src/app/core/store/patient/patient.actions';
import { PatientState } from 'src/app/core/store/patient/patient.state';
import { SignOut, SwitchPatient } from 'src/app/core/store/user/user.actions';
import { UserState } from 'src/app/core/store/user/user.state';
import { ApiStoreData } from 'src/app/core/store/utils';

@Component({
  selector: 'app-nav-bar',
  templateUrl: './nav-bar.component.html',
  styleUrls: ['./nav-bar.component.scss'],
})
export class NavBarComponent implements OnInit {
  @Select(UserState.isAgentUser) isAgentUser$!: Observable<boolean>;
  @Select(PatientState.patient) patient$!: Observable<Patient | null>;
  @Select(PatientState.patientsState) patients$!: Observable<ApiStoreData<Patient[]>>;

  SEPARATOR_ITEM: MenuItem = {
    separator: true,
  };

  DASHBOARD_NAV_ITEM: MenuItem = {
    label: 'Dashboard',
    routerLink: '/',
    automationId: 'dashboard-nav-link',
  };

  ORDERS_NAV_ITEM: MenuItem = {
    label: 'Orders',
    routerLink: '/orders',
    automationId: 'orders-nav-link',
  };

  PAYMENTS_NAV_ITEM: MenuItem = {
    label: 'Payments',
    routerLink: '/payments',
    automationId: 'payments-nav-link',
  };

  CONTACT_NAV_ITEM: MenuItem = {
    label: 'Contact Us',
    routerLink: '/contact',
    automationId: 'contact-us-nav-link',
  };

  PROFILE_NAV_ITEM: MenuItem = {
    label: 'Patient Profile',
    routerLink: '/profile',
    automationId: 'profile-nav-link',
  };

  // TODO: Add email avatar/text to account menu item in mobile
  // Consider using out custom menu in mobile entirely
  ACCOUNT_ITEM: MenuItem = {
    label: 'Manage Account',
    routerLink: '/account',
    automationId: 'account-nav-link',
  };

  SIGN_OUT_ITEM: MenuItem = {
    label: 'Sign Out',
    command: this.signOut.bind(this),
    automationId: 'log-out-button',
  };

  TEST_UTILS_ITEM: MenuItem = {
    label: 'Test Utils',
    automationId: 'test-utils-menu',
    items: [
      {
        label: 'Reset terms and conditions',
        command: this.resetTermsAndConditionsForTesting.bind(this),
        automationId: 'reset-terms-and-conditions-button',
      },
      {
        label: 'Test session expiration in 1 min',
        command: this.startSessionExpirationTest.bind(this),
        automationId: 'test-session-expiration-button',
      },
    ],
  };

  menuItems: MenuItem[] = [];
  isDesktopVersion: boolean = true;

  constructor(
    private store: Store,
    private envService: EnvService,
    private sessionExpirationService: SessionExpirationService,
    private responsiveUtils: ResponsiveUtilsService,
    private gtag: Gtag,
    private titlecase: TitleCasePipe
  ) {}

  ngOnInit() {
    combineLatest([this.isAgentUser$, this.patient$, this.responsiveUtils.isAboveXl()]).subscribe(
      ([isAgentUser, patient, isAboveXl]) => {
        if (!patient) {
          this.menuItems = [];
          return;
        }

        this.isDesktopVersion = isAboveXl;

        const useInternalPaymentPage = this.envService.features['makePaymentPage']
          ? this.envService.features['makePaymentPage'].enabled
          : false;

        if (this.isDesktopVersion) {
          const desktopMenuItems = [
            this.DASHBOARD_NAV_ITEM,
            this.ORDERS_NAV_ITEM,
            ...(useInternalPaymentPage ? [this.PAYMENTS_NAV_ITEM] : []),
            this.CONTACT_NAV_ITEM,
            ...(this.envService.isProduction() ? [] : [this.TEST_UTILS_ITEM]),
          ];

          this.menuItems = desktopMenuItems;
        } else {
          let mobileMenuItems = [
            this.DASHBOARD_NAV_ITEM,
            this.SEPARATOR_ITEM,
            this.ORDERS_NAV_ITEM,
            this.SEPARATOR_ITEM,
            ...(useInternalPaymentPage ? [this.PAYMENTS_NAV_ITEM, this.SEPARATOR_ITEM] : []),
            this.CONTACT_NAV_ITEM,
            this.SEPARATOR_ITEM,
          ];

          this.patients$.subscribe((patients) => {
            if (patients.data.length === 1) {
              mobileMenuItems = [...mobileMenuItems, this.PROFILE_NAV_ITEM];
            } else if (patients.data.length > 1) {
              mobileMenuItems = [
                ...mobileMenuItems,
                this.PROFILE_NAV_ITEM,
                {
                  label: 'Switch Patient',
                  expanded: true,
                  items: patients.data.map((p) => {
                    return {
                      label: `
                            <div class="flex align-items-center">
                              <div class="mr-2 p2 text-normal p-avatar user-menu-avatar-small">${p.initials}</div>
                              <div class="flex flex-column">
                                <div class="p4 font-normal">${this.titlecase.transform(p.fullName)}</div>
                                <div class="p4 font-normal text-grey-500">Patient ID: ${p.id}</div>
                              </div>
                              ${p.id === patient.id ? "<i class='ml-4 pi pi-check text-primary'></i>" : ''}
                            </div>
                          `,
                      disabled: p.id === patient.id,
                      escape: false,
                      command: this.switchPatient.bind(this, p.id),
                    } as MenuItem;
                  }),
                },
              ];
            }
          });

          if (!isAgentUser) {
            mobileMenuItems = [
              ...mobileMenuItems,
              this.SEPARATOR_ITEM,
              this.ACCOUNT_ITEM,
              this.SEPARATOR_ITEM,
              this.SIGN_OUT_ITEM,
            ];
          }

          this.menuItems = mobileMenuItems;
        }
      }
    );
  }

  switchPatient(pId: number) {
    const patientSwitched = new Subject<void>();

    this.patient$.pipe(takeUntil(patientSwitched)).subscribe((p) => {
      if (!!p && p.id === pId) return;

      patientSwitched.next();
      this.store.dispatch(new SwitchPatient(pId));
    });
  }

  signOut() {
    console.log('401 Logging: Initiating User SignOut from navbar...');
    this.store.dispatch(new SignOut());
    this.gtag.event('log_out', {
      event_category: 'engagement',
    });
  }

  resetTermsAndConditionsForTesting() {
    this.store.dispatch(new UpdatePatient({ acceptedTermsAndConditions: false }, { optimisticUpdate: false }));
  }

  startSessionExpirationTest() {
    this.sessionExpirationService.USER_IDLE_TIMER_VALUE_IN_MIN = 1;
    this.sessionExpirationService.WARNING_TIMER_VALUE_IN_MIN = 1;
    SessionExpirationService.runTimer = false;
    SessionExpirationService.runWarningTimer = false;
    this.sessionExpirationService.initilizeSessionTimeout();
  }
}
